/** Recurring occurrence helpers for CareHub booking dialog. */

import { getNowInAppTimezone } from "./dateFormat";

export const CAREHUB_RECURRING_OCCURRENCE_COUNT = 5;
export const DAYS_SERVICE_REQUIRED_MIN = 1;
export const DAYS_SERVICE_REQUIRED_MAX = 90;

export function clampDaysServiceRequired(value, fallback = 1) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(DAYS_SERVICE_REQUIRED_MAX, Math.max(DAYS_SERVICE_REQUIRED_MIN, n));
}

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Today's calendar date in the app's configured timezone (IST) — not the visitor's device clock. */
export function todayIsoLocal() {
  return getNowInAppTimezone().dateIso;
}

export function tomorrowIsoLocal() {
  return addDaysIso(todayIsoLocal(), 1);
}

export function addDaysIso(iso, days) {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  const ny = dt.getFullYear();
  const nm = String(dt.getMonth() + 1).padStart(2, "0");
  const nd = String(dt.getDate()).padStart(2, "0");
  return `${ny}-${nm}-${nd}`;
}

export function dayOfWeekFromIso(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).getDay();
}

export function recurringPatternNeedsDatePicker(recurringPattern) {
  return recurringPattern === "alternate" || recurringPattern === "custom";
}

export function recurringPatternNeedsWeeklyDay(recurringPattern) {
  return recurringPattern === "weekly";
}

export function recurringPatternIsAuto(recurringPattern) {
  return recurringPattern === "daily" || recurringPattern === "weekdays";
}

/**
 * Build occurrence rows `{ serviceDate, startTime, endTime }` for recurring bookings.
 */
export function buildRecurringOccurrences({
  recurringPattern,
  startDate,
  startTime,
  endTime,
  selectedDates = [],
  weeklyDayOfWeek = 1,
  count = CAREHUB_RECURRING_OCCURRENCE_COUNT,
} = {}) {
  if (!startDate || !startTime || !endTime) return [];

  if (recurringPattern === "custom" || recurringPattern === "alternate") {
    return [...selectedDates]
      .sort()
      .slice(0, count)
      .map((serviceDate) => ({ serviceDate, startTime, endTime }));
  }

  if (recurringPattern === "weekly") {
    const out = [];
    let cursor = startDate;
    const targetDow = Number(weeklyDayOfWeek);
    while (out.length < count) {
      if (dayOfWeekFromIso(cursor) === targetDow) {
        out.push({ serviceDate: cursor, startTime, endTime });
      }
      cursor = addDaysIso(cursor, 1);
      if (out.length === 0 && cursor > addDaysIso(startDate, 370)) break;
    }
    return out;
  }

  if (recurringPattern === "weekdays") {
    const out = [];
    let cursor = startDate;
    while (out.length < count) {
      const dow = dayOfWeekFromIso(cursor);
      if (dow >= 1 && dow <= 5) {
        out.push({ serviceDate: cursor, startTime, endTime });
      }
      cursor = addDaysIso(cursor, 1);
    }
    return out;
  }

  // daily (default)
  return Array.from({ length: count }, (_, i) => ({
    serviceDate: addDaysIso(startDate, i),
    startTime,
    endTime,
  }));
}

export function formatOccurrenceSummary(occurrences = []) {
  if (!occurrences.length) return "";
  if (occurrences.length === 1) return occurrences[0].serviceDate;
  return `${occurrences.length} visits · ${occurrences[0].serviceDate} → ${occurrences[occurrences.length - 1].serviceDate}`;
}

export { WEEKDAY_NAMES };
